import React, { useMemo, useState } from 'react';
import { AgCharts } from 'ag-charts-react';
import { AgChartOptions } from 'ag-charts-community';
import { sortBy } from 'lodash';
import { TPhasePercentagesItem } from '../../../kpiTypes';
import { formatValueAsString } from '../../../../../utils/helpers';
import { ChartContainer } from '../../../../../shared/components/charts/ChartContainer';

export interface IPolygonCallsOperationalPhaseChartProps {
	chartKey: number;
	phasePercentagesData: TPhasePercentagesItem[];
}

const PolygonCallsOperationalPhaseChart: React.FC<IPolygonCallsOperationalPhaseChartProps> = ({ ...props }) => {
	const formatOptions: Intl.NumberFormatOptions = { minimumFractionDigits: 0, maximumFractionDigits: 1 };

	const phaseColors: { [key: string]: string } = {
		'At Anchor': '#183D57',
		Moored: '#F0D035',
		Maneouvring: '#569FD3',
		Unclassified: '#3F4B4B',
	};

	const orderedData = useMemo(() => {
		if (!props.phasePercentagesData) return [];
		else {
			const dataListOrdered = sortBy(
				props.phasePercentagesData.filter((x) => x.countOfPhase > 0),
				['countOfPhase'],
			).reverse() as TPhasePercentagesItem[];

			return dataListOrdered;
		}
	}, [props.phasePercentagesData]);

	const [options] = useState<AgChartOptions>({
		data: orderedData,
		series: [
			{
				type: 'donut',
				calloutLabelKey: 'operationalPhase',
				angleKey: 'percentOfPhase',
				innerRadiusRatio: 0.6,
				fills: orderedData ? orderedData.map((phase) => phaseColors[phase.operationalPhase] || '#000000') : [],
				calloutLine: {
					colors: orderedData
						? orderedData.map((phase) => phaseColors[phase.operationalPhase] || '#000000')
						: ['#000000', '#000000', '#000000', '#000000'],
				},
				tooltip: {
					renderer: ({ datum, angleKey, calloutLabelKey = 'operationalPhase' }) => ({
						data: undefined,
						title: `${datum[calloutLabelKey]}`,
						heading: formatValueAsString(datum[angleKey], formatOptions, '', '', '%'),
					}),
				},
				calloutLabel: {
					avoidCollisions: true,
					formatter: ({ datum, angleKey }) => formatValueAsString(datum[angleKey], formatOptions, '', '', '%'), // Shows percentage on the chart
				},
			},
		],

		legend: {
			position: 'bottom',
			item: {
				label: {
					formatter: (params) => {
						const item = orderedData.find((_, index) => index === params.itemId);
						return item ? item.operationalPhase : params.itemId;
					},
				},
			},
		},
	});

	return (
		<ChartContainer
			data={options.data}
			filename="polygon_calls_operational_phase_data"
			buttonPosition="top-right"
		>
			<AgCharts key={props.chartKey} options={options} className="w-full h-full" />
		</ChartContainer>
	);
};

export default PolygonCallsOperationalPhaseChart;
